import { useQuery, useMutation } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { PaymentCard, InsertPaymentCard, LicensePaymentCard, InsertLicensePaymentCard, License } from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

export function usePaymentCards() {
  const { toast } = useToast();
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null);
  const [selectedCard, setSelectedCard] = useState<PaymentCard | null>(null);
  const [cardLicenses, setCardLicenses] = useState<License[]>([]);
  const [monthlySpend, setMonthlySpend] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Fetch all payment cards
  const { 
    data: cards = [], 
    isLoading: isLoadingCards,
    error: cardsError,
    refetch: refetchCards
  } = useQuery<PaymentCard[]>({
    queryKey: ['/api/payment-cards'],
  });

  // Create a new payment card
  const createCardMutation = useMutation({
    mutationFn: async (card: InsertPaymentCard) => {
      return await apiRequest("/api/payment-cards", {
        method: "POST",
        body: card
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/payment-cards'] });
      queryClient.invalidateQueries({ queryKey: ['/api/dashboard/card-renewals'] });
      toast({
        title: "Success",
        description: "Payment card added successfully",
      });
    },
    onError: (error: any) => {
      // Check for duplicate card error
      if (error.message && error.message.includes('409')) {
        toast({
          title: "Duplicate Card",
          description: "A payment card with the same last four digits and expiry date already exists for your account.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: `Failed to add payment card: ${error.message}`,
          variant: "destructive",
        });
      }
    },
  });

  // Update a payment card
  const updateCardMutation = useMutation({
    mutationFn: async ({ id, card }: { id: number; card: Partial<InsertPaymentCard> }) => {
      return await apiRequest(`/api/payment-cards/${id}`, {
        method: "PUT",
        body: card
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/payment-cards'] });
      queryClient.invalidateQueries({ queryKey: ['/api/dashboard/card-renewals'] });
      toast({
        title: "Success",
        description: "Payment card updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: `Failed to update payment card: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  // Delete a payment card
  const deleteCardMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest(`/api/payment-cards/${id}`, {
        method: "DELETE"
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/payment-cards'] });
      queryClient.invalidateQueries({ queryKey: ['/api/dashboard/card-renewals'] });
      toast({
        title: "Success",
        description: "Payment card deleted successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: `Failed to delete payment card: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  // Set a card as default
  const setDefaultCardMutation = useMutation({
    mutationFn: async (id: number) => {
      return await apiRequest(`/api/payment-cards/${id}/set-default`, {
        method: "POST"
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/payment-cards'] });
      queryClient.invalidateQueries({ queryKey: ['/api/dashboard/card-renewals'] });
      toast({
        title: "Success",
        description: "Default payment card updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: `Failed to set default payment card: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  // Get card details
  const getCardDetails = async (id: number) => {
    try {
      return await apiRequest(`/api/payment-cards/${id}`);
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to fetch card details: ${(error as Error).message}`,
        variant: "destructive",
      });
      throw error;
    }
  };

  // Get licenses associated with a card
  const getCardLicenses = async (id: number) => {
    try {
      return await apiRequest(`/api/payment-cards/${id}/licenses`, {
        method: "GET"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to fetch card licenses: ${(error as Error).message}`,
        variant: "destructive",
      });
      throw error;
    }
  };

  // Get card monthly spend
  const getCardMonthlySpend = async (id: number) => {
    try {
      return await apiRequest(`/api/payment-cards/${id}/monthly-spend`, {
        method: "GET"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to fetch monthly spend: ${(error as Error).message}`,
        variant: "destructive",
      });
      throw error;
    }
  };

  // Assign a card to a license
  const assignCardToLicenseMutation = useMutation({
    mutationFn: async (data: InsertLicensePaymentCard) => {
      return await apiRequest('/api/license-payment-cards', {
        method: 'POST',
        body: data
      });
    },
    onSuccess: (_, variables) => {
      // Invalidate both payment cards and licenses queries
      queryClient.invalidateQueries({ queryKey: ['/api/payment-cards'] });
      queryClient.invalidateQueries({ queryKey: ['/api/licenses'] });
      
      // Also invalidate the specific license payment-card query
      queryClient.invalidateQueries({ 
        queryKey: ['/api/licenses', variables.licenseId, 'payment-card'] 
      });
      
      toast({
        title: "Success",
        description: "Payment card assigned to license successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: `Failed to assign card to license: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  // Get payment card assigned to a license
  const getLicenseCard = async (licenseId: number) => {
    try {
      return await apiRequest(`/api/licenses/${licenseId}/payment-card`, {
        method: "GET"
      });
    } catch (error) {
      // Handle 404s silently as they just mean no card is assigned
      if (error instanceof Response && error.status === 404) {
        return null;
      }
      
      // For other errors, log them but don't show a toast to the user
      // This prevents the error message when editing licenses
      console.error(`Error fetching license payment card: ${(error as Error).message}`);
      return null;
    }
  };

  // Load card details, licenses, and monthly spend when a card is selected
  useEffect(() => {
    const loadCardDetails = async () => {
      if (!selectedCardId) {
        setSelectedCard(null);
        setCardLicenses([]);
        setMonthlySpend(0);
        return;
      }

      setIsLoading(true);
      try {
        setError(null); // Reset error state before fetch
        
        // Fetch card details
        const card = await getCardDetails(selectedCardId);
        setSelectedCard(card);

        // Fetch card licenses
        const licenses = await getCardLicenses(selectedCardId);
        setCardLicenses(licenses);

        // Fetch monthly spend
        const spend = await getCardMonthlySpend(selectedCardId);
        setMonthlySpend(spend);
      } catch (error) {
        console.error("Error loading card details:", error);
        setError(error as Error);
        toast({
          title: "Error",
          description: "Could not load card details",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadCardDetails();
  }, [selectedCardId]);

  // Update the error state if there's an error from the cards query
  useEffect(() => {
    if (cardsError) {
      setError(cardsError as Error);
    }
  }, [cardsError]);

  return {
    cards,
    isLoadingCards,
    cardsError,
    refetchCards,
    createCardMutation,
    updateCardMutation,
    deleteCardMutation,
    setDefaultCardMutation,
    getCardDetails,
    getCardLicenses,
    getCardMonthlySpend,
    assignCardToLicenseMutation,
    getLicenseCard,
    selectedCardId,
    setSelectedCardId,
    selectedCard,
    cardLicenses,
    monthlySpend,
    isLoading,
    error,
  };
}
